// apps/web/src/pages/spread/settings/GrantsList.tsx
//
// Phase 9 Plan 09-12 Task 2 — Grants list inside the Sharing tab.
//
// DESIGN PER 09-12-PLAN <interfaces> NOTE:
//   The Sharing tab does NOT submit grant changes through the spread save body
//   (Q-07 atomic PUT doesn't carry inline grants). Instead it calls Phase 5
//   /api/spreads/:id/grants/* endpoints directly via TanStack Query/Mutation.
//   This file is the read + revoke half; SharingTab.tsx owns the add-grant
//   input and visibility radio. Both share the same TanStack queryKey so
//   mutations invalidate the list automatically.
//
// FETCH PATH:
//   GET /api/spreads/:id/grants → returns { userGrants: [...], groupGrants: [...] }
//   (the Phase 5 list endpoint shape — see apps/api/src/services/grants.ts
//    listGrantsOnSpread). Each user grant carries { userId, permission, email? }.
//
// REVOKE FLOW (two-stage armed confirm per UI-SPEC line 428):
//   1. Click X icon → row arms (background flips to destructive, 4s timer).
//   2. Within 4s: second click confirms → DELETE /api/spreads/:id/grants/user/:userId.
//   3. After 4s: arm clears (no destructive action).
//   The pattern matches the summary-checklist Clear-checkmarks two-stage
//   confirm (apps/web/src/widgets/summary-checklist/renderer.tsx lines 205-223).

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { X } from "lucide-react";
import { useEffect, useState } from "react";
import { api } from "@/lib/api-client";
import { useTerm } from "@/lib/vocabulary/useTerm";

// Shape returned by GET /api/spreads/:id/grants (Phase 5 listGrantsOnSpread).
// Defensive structural typing — the API may evolve; this file uses just the
// fields it cares about and silently ignores unknown keys.
interface SpreadGrantsListResponse {
  userGrants?: Array<{
    userId?: string;
    user_id?: string;
    permission: string;
    email?: string;
  }>;
  groupGrants?: Array<{
    groupId?: string;
    group_id?: string;
    permission: string;
    name?: string;
  }>;
}

export interface GrantsListProps {
  /** Spread id whose grants are listed. Used as TanStack queryKey + URL. */
  spreadId: string;
}

const ARM_TIMEOUT_MS = 4000;

export function GrantsList({ spreadId }: GrantsListProps): React.ReactElement {
  const qc = useQueryClient();
  const container = useTerm("container");
  const [armedUserId, setArmedUserId] = useState<string | null>(null);

  // Disarm after timeout.
  useEffect(() => {
    if (armedUserId === null) return;
    const t = setTimeout(() => setArmedUserId(null), ARM_TIMEOUT_MS);
    return () => clearTimeout(t);
  }, [armedUserId]);

  const grantsQuery = useQuery<SpreadGrantsListResponse>({
    queryKey: ["spread", spreadId, "grants"],
    queryFn: () => api<SpreadGrantsListResponse>(`/api/spreads/${spreadId}/grants`),
    // The grants list is small and reactive to the user opening Sharing tab;
    // refetchOnWindowFocus is the React Query default which is fine here.
  });

  const revokeMutation = useMutation<unknown, Error, string>({
    mutationFn: async (subjectUserId) => {
      return api<unknown>(`/api/spreads/${spreadId}/grants/user/${subjectUserId}`, {
        method: "DELETE",
      });
    },
    onSuccess: () => {
      setArmedUserId(null);
      void qc.invalidateQueries({ queryKey: ["spread", spreadId, "grants"] });
    },
  });

  function handleRevokeClick(userId: string): void {
    if (armedUserId === userId) {
      revokeMutation.mutate(userId);
    } else {
      setArmedUserId(userId);
    }
  }

  if (grantsQuery.isLoading) {
    return (
      <p className="text-xs text-muted-foreground" data-grants-loading>
        Loading access list…
      </p>
    );
  }
  if (grantsQuery.isError) {
    return (
      <p className="text-xs text-destructive" data-grants-error>
        Couldn't load the access list.
      </p>
    );
  }

  const userGrants = grantsQuery.data?.userGrants ?? [];
  if (userGrants.length === 0) {
    return (
      <p className="text-xs text-muted-foreground" data-grants-empty>
        No one else has access yet.
      </p>
    );
  }

  return (
    <ul className="flex flex-col gap-1" data-grants-list>
      {userGrants.map((grant) => {
        const uid = grant.userId ?? grant.user_id ?? "";
        const armed = armedUserId === uid;
        return (
          <li
            key={uid}
            className="flex items-center justify-between gap-2 rounded-md border border-input bg-card px-3 py-2"
          >
            <div className="flex flex-col">
              <span className="text-sm">{grant.email ?? uid}</span>
              <span className="text-xs text-muted-foreground">{grant.permission}</span>
            </div>
            <button
              type="button"
              aria-label={`Revoke access for ${grant.email ?? uid}`}
              onClick={() => handleRevokeClick(uid)}
              className={`inline-flex items-center justify-center rounded-md p-2 text-xs font-medium transition-colors ${
                armed
                  ? "bg-destructive text-destructive-foreground"
                  : "bg-transparent text-muted-foreground hover:text-destructive"
              }`}
            >
              <X size={14} aria-hidden />
              <span className="sr-only">
                {armed ? `Confirm revoke ${container} access` : "Revoke access"}
              </span>
              {armed ? (
                <span className="ml-1" aria-hidden>
                  Confirm
                </span>
              ) : null}
            </button>
            {armed ? (
              <span className="sr-only" aria-live="polite">
                Confirm revoke
              </span>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}
GrantsList.displayName = "GrantsList";
